import { AdminTreeNode, AnalysisJobAreaInputType } from "@/types/analysis";
import {
  DatasetAttribute,
  PayloadAttributes,
  PolygonFeature,
  PolygonUI
} from "@/types/analysisHistory";
import { AnalysisResultType } from "@/types/dataset";

export const mapAnalysisInputType = (
  input?: string
): AnalysisJobAreaInputType | null => {
  switch (input) {
    case "catchment":
    case "custom_catchment":
      return AnalysisJobAreaInputType.CATCHMENT;
    case "adm_area":
      return AnalysisJobAreaInputType.ADMIN;
    case "polygon":
      return AnalysisJobAreaInputType.POLYGON;
    default:
      return null;
  }
};

export const mapAnalysisResultType = (
  output?: string
): AnalysisResultType | null => {
  switch (output) {
    case "site_profiling":
      return AnalysisResultType.TYPE_SITE_PROFILING;
    case "grid":
      return AnalysisResultType.TYPE_GRID;
    case "geohash":
      return AnalysisResultType.TYPE_GEOHASH;
    case "h3":
      return AnalysisResultType.TYPE_H3;
    default:
      return null;
  }
};

export const mapFeaturesToPolygons = (
  features: PolygonFeature[]
): PolygonUI[] => {
  return features.map((feature) => {
    const coords = feature?.geometry?.coordinates;
    const parsed: number[][][] =
      typeof coords === "string"
        ? JSON.parse(coords)
        : (coords as number[][][]);
    const ring = parsed[0];
    const ringString = ring
      .map(([longitude, latitude]) => `${longitude} ${latitude}`)
      .join(", ");
    return {
      name: feature.properties?.name ?? "Polygon",
      coordinates: `((${ringString}))`
    };
  });
};

export const formatLocationLabel = (location: {
  name?: string | null;
  latitude: number;
  longitude: number;
}) => {
  const coordinate = `${location.latitude}, ${location.longitude}`;
  if (location.name && location.name.trim() !== "") {
    return `${location.name} - (${coordinate})`;
  }
  return coordinate;
};

export const getAdminAreaCriteria = (
  payload: PayloadAttributes
): string[][] => {
  if (!payload?.attributes?.length) return [];

  const adminAreaAttribute = payload.attributes.find(
    (attr) => attr.attribute_code === "adm_area_id"
  );

  const criteria = adminAreaAttribute?.filter?.criteria;
  if (!criteria) return [];

  const adminAreas = criteria
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .sort((a, b) => {
      const partsA = a.split("|").map((s) => s.trim().toLowerCase());
      const partsB = b.split("|").map((s) => s.trim().toLowerCase());

      const len = Math.max(partsA.length, partsB.length);
      for (let i = 0; i < len; i++) {
        const valA = partsA[i] ?? "";
        const valB = partsB[i] ?? "";
        if (valA < valB) return -1;
        if (valA > valB) return 1;
      }
      return 0;
    })
    .map((item) => item.split("|").map((s) => s.trim()));

  return adminAreas;
};

const hasCriteria = (
  attr: DatasetAttribute
): attr is DatasetAttribute & { filter: { criteria: string } } =>
  Boolean(attr.filter?.criteria?.trim());

export const getAttributeFilters = (
  payload: PayloadAttributes
): Array<{ label: string; value: string }> => {
  if (!payload?.attributes?.length) return [];

  return payload.attributes
    .filter((attr) => attr.attribute_code !== "adm_area_id")
    .filter(hasCriteria)
    .map((attr) => ({
      label: attr.name,
      value: attr.filter.criteria
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean)
        .join(", ")
    }));
};

export const buildTreeFromArray = (paths: string[][]): AdminTreeNode[] => {
  const root: AdminTreeNode[] = [];

  for (const path of paths) {
    let currentLevel = root;

    for (const name of path) {
      let existing = currentLevel.find((node) => node.name === name);
      if (!existing) {
        existing = { name, children: [] };
        currentLevel.push(existing);
      }
      currentLevel = existing.children;
    }
  }

  return root;
};
